/**
 * Validation of `@name(...)` annotations into `NodeMetadata`.
 *
 * This is a security boundary as much as a convenience: annotation values end
 * up as SVG attributes and as text in the DOM, so anything that is not on an
 * explicit allow-list is rejected here and reported as a diagnostic rather than
 * being passed through. No value reaching `NodeMetadata` is ever interpolated
 * into markup, and none of them can carry a URL scheme other than http/https/mailto.
 */
import type { Annotation, MetadataShape, NodeMetadata } from '../ast/nodes';
import { METADATA_SHAPES } from '../ast/nodes';
import type { Diagnostic } from '../diagnostics/types';
import { DiagnosticCode } from '../diagnostics/types';

/** Icon slugs the renderer knows how to draw. */
export const ICON_SLUGS = [
  'user',
  'database',
  'mail',
  'lock',
  'clock',
  'alert',
  'check',
  'card',
  'file',
  'cloud',
  'search',
  'settings',
  'code',
  'chart',
  'cart',
  'key',
  'shield',
  'bell',
  'tag',
  'star',
] as const;

export type IconSlug = (typeof ICON_SLUGS)[number];

const ICON_SET = new Set<string>(ICON_SLUGS);
const SHAPE_SET = new Set<string>(METADATA_SHAPES);

export const ANNOTATION_NAMES = ['color', 'icon', 'shape', 'id', 'link', 'note', 'class'] as const;

const ANNOTATION_SET = new Set<string>(ANNOTATION_NAMES);

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const SAFE_ID = /^[A-Za-z][\w-]{0,63}$/;
const SAFE_CLASS_TOKEN = /^[A-Za-z][\w-]{0,31}$/;
const SAFE_URL_SCHEME = /^(?:https?:|mailto:)/i;

const MAX_NOTE_LENGTH = 400;

export interface MetadataResult {
  readonly metadata: NodeMetadata;
  readonly diagnostics: readonly Diagnostic[];
}

export function resolveMetadata(annotations: readonly Annotation[]): MetadataResult {
  if (annotations.length === 0) {
    return { metadata: {}, diagnostics: [] };
  }

  const diagnostics: Diagnostic[] = [];
  const draft: {
    color?: string;
    icon?: string;
    shape?: MetadataShape;
    id?: string;
    link?: string;
    note?: string;
    className?: string;
  } = {};

  for (const annotation of annotations) {
    const { name, args, range, rawName } = annotation;

    if (!ANNOTATION_SET.has(name)) {
      diagnostics.push({
        code: DiagnosticCode.UnknownAnnotation,
        severity: 'warning',
        message: `Unknown annotation \`@${rawName}\`. It will be ignored.`,
        range,
        suggestion: `Known annotations: ${ANNOTATION_NAMES.map((n) => `@${n}`).join(', ')}.`,
        params: { name: rawName },
      });
      continue;
    }

    const value = args[0];
    if (value === undefined || value.trim() === '') {
      diagnostics.push({
        code: DiagnosticCode.InvalidAnnotationValue,
        severity: 'warning',
        message: `\`@${rawName}\` needs a value, for example \`@${name}("...")\`.`,
        range,
        params: { name: rawName },
      });
      continue;
    }

    switch (name) {
      case 'color': {
        const color = value.trim();
        if (!HEX_COLOR.test(color)) {
          diagnostics.push(
            invalid(rawName, color, range, 'Use a hex colour such as `#2563eb` or `#25b`.'),
          );
          break;
        }
        draft.color = color.toLowerCase();
        break;
      }
      case 'icon': {
        const icon = value.trim().toLowerCase();
        if (!ICON_SET.has(icon)) {
          diagnostics.push(
            invalid(rawName, icon, range, `Known icons: ${ICON_SLUGS.slice(0, 8).join(', ')}, …`),
          );
          break;
        }
        draft.icon = icon;
        break;
      }
      case 'shape': {
        const shape = value.trim().toLowerCase();
        if (!SHAPE_SET.has(shape)) {
          diagnostics.push(
            invalid(rawName, shape, range, `Valid shapes: ${METADATA_SHAPES.join(', ')}.`),
          );
          break;
        }
        draft.shape = shape as MetadataShape;
        break;
      }
      case 'id': {
        const id = value.trim();
        if (!SAFE_ID.test(id)) {
          diagnostics.push(
            invalid(
              rawName,
              id,
              range,
              'Ids start with a letter and may contain letters, digits, `_` and `-`.',
            ),
          );
          break;
        }
        draft.id = id;
        break;
      }
      case 'link': {
        const link = value.trim();
        if (!SAFE_URL_SCHEME.test(link)) {
          diagnostics.push(
            invalid(rawName, link, range, 'Links must start with `https:`, `http:` or `mailto:`.'),
          );
          break;
        }
        // Reject anything the URL parser cannot make sense of, so the renderer
        // never receives a half-formed href.
        try {
          const parsed = new URL(link);
          if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
            diagnostics.push(invalid(rawName, link, range, 'Unsupported URL scheme.'));
            break;
          }
          draft.link = parsed.toString();
        } catch {
          diagnostics.push(invalid(rawName, link, range, 'That is not a valid URL.'));
        }
        break;
      }
      case 'note': {
        const note = value.trim().slice(0, MAX_NOTE_LENGTH);
        draft.note = note;
        break;
      }
      case 'class': {
        const tokens = value
          .trim()
          .split(/\s+/)
          .filter((t) => t.length > 0);
        const safe = tokens.filter((t) => SAFE_CLASS_TOKEN.test(t));
        if (safe.length !== tokens.length) {
          diagnostics.push(
            invalid(
              rawName,
              value,
              range,
              'Class names start with a letter and may contain letters, digits, `_` and `-`.',
            ),
          );
        }
        if (safe.length > 0) draft.className = safe.join(' ');
        break;
      }
    }
  }

  return { metadata: Object.freeze({ ...draft }), diagnostics };
}

function invalid(
  name: string,
  value: string,
  range: Annotation['range'],
  suggestion: string,
): Diagnostic {
  return {
    code: DiagnosticCode.InvalidAnnotationValue,
    severity: 'warning',
    message: `\`@${name}\` does not accept ${JSON.stringify(value)}.`,
    range,
    suggestion,
    params: { name, value },
  };
}
